import { Metrics } from "../analytics/Metrics";
import Button from "../core/Button";
import GameConfig from "../core/data/GameConfig";
import ResizableScene from "../core/ResizableScene"
import ScaleManager from "../core/ScaleManager";
import SoundButton from "../core/SoundButton";
import SessionData from "../SessionData";
import PhotoSelectCarousel from "../ui/PhotoSelectCarousel";
import UsePhotoPopup from "../ui/UsePhotoPopup";

export default class PhotoSelectScene extends ResizableScene {

    protected photoSelect: PhotoSelectCarousel;
    yourPhotoButton: Button
    hasCameraEnabled: boolean;
    leftClutter: Phaser.GameObjects.Image;
    rightClutter: Phaser.GameObjects.Image;
    buttons: (Button | Phaser.GameObjects.Image)[];
    create(): void {
        Metrics.analytics.logEvent("visit_screen", { game: "ag_photobooth", screen: "photo_select" });
        this.music.play("menuMusic");

        const bg = this.add.image(0, 0, "takePhotoBG");
        const leftClutter = this.leftClutter = this.add.image(0, 0, "leftClutter");
        const rightClutter = this.rightClutter = this.add.image(0, 0, "rightClutter");

		const carosuel = this.photoSelect = new PhotoSelectCarousel(this, 0);
		this.add.existing(carosuel);

        const photoButton = this.add.button(0, 0, "takePhotoButton", undefined, this.play, this, true);
        photoButton.clickSoundKey = "takePhotoBtn";
        const yourPhotoButton = this.yourPhotoButton = this.add.button(0, 0, "takePhotoYourPhoto", undefined, this.activateCamera, this, true);
        this.yourPhotoButton.setAlpha(0.5);
        this.yourPhotoButton.disableInteractive();
        const photoArrow = this.add.image(0, 0, "takePhotoArrow");

        const soundImg = this.sound.mute ? "soundBtnOff" : "soundBtnOn";
        const soundButton = new SoundButton(this, 0, 0, soundImg, (mute) => {
            soundButton.setTexture(mute ? "soundBtnOff" : "soundBtnOn");
        });
        this.add.existing(soundButton);

		const backButton = this.add.button(0, 0, "takePhotoBack", undefined, () => {
            backButton.disableInteractive();
            this.exit(); 
        }, this);
        this.buttons = [photoButton, backButton, soundButton, yourPhotoButton, photoArrow];

        this.addResize((): void => {
            ScaleManager.ScaleBackgroundFromZone(bg, this.viewport);
            this.alignIn.Center(bg, this.viewport);
            this.alignIn.LeftCenter(leftClutter, this.viewport, 50, 0);
            this.alignIn.RightCenter(rightClutter, this.viewport, 50, 0);
			this.alignIn.Center(carosuel, this.viewport);
            this.alignIn.BottomRight(photoButton, this.viewport, -32, -150);
            this.alignTo.TopCenter(photoArrow, photoButton, 0, 0);
            this.alignIn.BottomLeft(yourPhotoButton, this.viewport, -32, -150);
            this.alignIn.TopRight(soundButton, this.viewport, -32, -19);
			this.alignIn.TopLeft(backButton, this.viewport, -32, -19);
        });

        this.tweens.add({
            targets: photoArrow,
            y: photoArrow.y - 40,
            ease: Phaser.Math.Easing.Quadratic.InOut,
            loop: -1,
            yoyo: true,
            duration: 1000,
        });

        super.create();

        // Build in animation
        leftClutter.x -= leftClutter.width;
        rightClutter.x += rightClutter.width;       

        this.tweens.add({
            targets: leftClutter,
            x: leftClutter.x + leftClutter.width,
            duration: 400,
            delay: 50,
            ease: Phaser.Math.Easing.Back.Out,
        });

        this.tweens.add({
            targets: rightClutter,
            x: rightClutter.x - rightClutter.width,
            duration: 400,
            delay: 50,
            ease: Phaser.Math.Easing.Back.Out,
        });

        for (const obj of this.photoSelect.photoTweenObjs) {
            const oldPhotoScale = obj.scale;
            obj.setAngle(80);
            obj.setScale(0);
            this.tweens.add({
                targets: obj,
                angle: 0,
                scale: oldPhotoScale,
                duration: 400,
                ease: Phaser.Math.Easing.Back.Out,
            });
        }

        for (const obj of this.photoSelect.tweenUpObjs) {
            obj.y += 200;
            this.tweens.add({
                targets: obj,
                y: obj.y - 200,
                duration: 400,
                ease: Phaser.Math.Easing.Quadratic.Out,
            });
        }

        for (const button of this.buttons) {
            button.setScale(0);
            this.tweens.add({
                targets: button,
                scale: 1,
                duration: 400,
                delay: 200,
                ease: Phaser.Math.Easing.Back.Out,
            });
        }
        
    }

    tweenOut(onComplete: Function): void {
        this.tweens.add({
            targets: this.leftClutter,
            x: this.leftClutter.x - this.leftClutter.width,
            duration: 400,
            delay: 300,
            ease: Phaser.Math.Easing.Back.In,
        });

        this.tweens.add({
            targets: this.rightClutter,
            x: this.rightClutter.x + this.rightClutter.width,
            duration: 400,
            delay: 300,
            ease: Phaser.Math.Easing.Back.In,
            onComplete: () => {
                console.log("onComplete called!");
                onComplete();
            }
        });

        this.tweens.add({
            targets: [this.photoSelect.displayText, this.photoSelect.photoHeader],
            scale: 0,
            duration: 400,
            delay: 300,
            ease: Phaser.Math.Easing.Back.In,
        });

        for (const obj of this.photoSelect.photoTweenObjs) {
            this.tweens.add({
                targets: obj,
                angle: 80,
                scale: 0,
                duration: 400,
                delay: 300,
                ease: Phaser.Math.Easing.Back.In,
            });
        }

        for (const obj of this.photoSelect.tweenUpObjs) {
            this.tweens.add({
                targets: obj,
                y: obj.y + 200,
                duration: 400,
                delay: 300,
                ease: Phaser.Math.Easing.Quadratic.In,
            });
        }

        for (const button of this.buttons) {
            this.tweens.add({
                targets: button,
                scale: 0,
                duration: 400,
                delay: 300,
                ease: Phaser.Math.Easing.Back.In,
            });
        }
    }

    exit(): void {
        // TODO Exit
        this.tweenOut(() => this.scene.start("SplashScene"));
	}

    activateCamera(): void {
        this.yourPhotoButton.setAlpha(0.5);
        this.yourPhotoButton.disableInteractive();
        this.photoSelect.userMediaContainer.setVisible(true);
        this.photoSelect.currentPhoto.enablePhoto(false);
        this.photoSelect.photo.setTexture("waitingForCamera");
        if(!this.hasCameraEnabled){
            this.photoSelect.photoHeader.setVisible(false);
            this.photoSelect.displayText.setVisible(false);
        }
    }

    disableCamera(): void {
        this.yourPhotoButton.setAlpha(1);
        this.yourPhotoButton.setInteractive();
        this.photoSelect.userMediaContainer.setVisible(false);
    }

    capture(): string {
		//console.log(this.photoSelect.userMediaContainer);
		if(this.photoSelect.userMediaContainer){
			const size = this.photoSelect.userMediaContainer.getStreamSize();
			const key = 'userVideoFeed';
			const snapshot = this.photoSelect.userMediaContainer.snapshot(key, size.x, size.y);
			if (snapshot != null) return key;
            else return "waitingForCamera";
		}
	}

    play(): void {
        // this.soundManager.play("playButton");
        const currentPhoto = this.photoSelect.currentPhoto;
        if (!currentPhoto) {
            if (this.photoSelect.userMediaContainer.status != "success") return;
            SessionData.Instance.selectedPhoto = this.capture();
            Metrics.analytics.logEvent("took_photo", { game: "ag_photobooth", subject: "player" });
        }
        else {
            SessionData.Instance.selectedPhoto = currentPhoto.photoKey;
            const subjectName = GameConfig.get<{ [key: string]: string }>("dollPhotoNames")[currentPhoto.photoKey] ?? currentPhoto.photoKey;
            Metrics.analytics.logEvent("took_photo", { game: "ag_photobooth", subject: subjectName });
        }
        const popup = new UsePhotoPopup(this);
        this.add.existing(popup);
    }

    shutdown(): void {
        super.shutdown();
    }
}